import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Layout from '../components/Layout';
import apartmentService from '../services/apartmentService';
import { useUnion } from '../contexts/UnionContext';

const THIS_YEAR = new Date().getFullYear();
const THIS_MONTH = new Date().getMonth() + 1;

function Apartments() {
  const { t } = useTranslation();
  usePageTitle(t('apartments.title'));
  const navigate = useNavigate();
  const { activeUnion, isUnionAdmin } = useUnion();
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invoice dialog state
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, apartment: null });
  const [invoiceYear, setInvoiceYear] = useState(THIS_YEAR);
  const [invoiceMonth, setInvoiceMonth] = useState(THIS_MONTH);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadApartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUnion]);

  const loadApartments = async () => {
    if (!activeUnion) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apartmentService.getApartments();
      setApartments(Array.isArray(data) ? data : data.results || []);
      setError('');
    } catch (err) {
      console.error('Failed to load apartments:', err);
      setError(t('apartments.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (id) => navigate(`/apartments/${id}`);
  const handleEdit = (id) => navigate(`/apartments/${id}/edit`);
  const handleCreate = () => navigate('/apartments/new');

  const openInvoiceDialog = (apartment) => {
    setInvoiceYear(THIS_YEAR);
    setInvoiceMonth(THIS_MONTH);
    setInvoiceDialog({ open: true, apartment });
  };
  const closeInvoiceDialog = () => setInvoiceDialog({ open: false, apartment: null });

  const handlePreview = async () => {
    const { apartment } = invoiceDialog;
    if (!apartment) return;
    setPreviewing(true);
    try {
      const blob = await apartmentService.previewInvoice(apartment.id, invoiceYear, invoiceMonth);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setSnackbar({ open: true, message: t('apartments.failedPreview'), severity: 'error' });
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    const { apartment } = invoiceDialog;
    if (!apartment) return;
    setSending(true);
    try {
      const result = await apartmentService.sendInvoice(apartment.id, invoiceYear, invoiceMonth);
      closeInvoiceDialog();
      setSnackbar({
        open: true,
        message: t('apartments.invoiceSent', { recipients: result.recipients.join(', ') }),
        severity: 'success',
      });
    } catch (err) {
      const msg = err.response?.data?.error || t('apartments.failedSend');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  if (!activeUnion) {
    return (
      <Layout>
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">{t('apartments.selectUnion')}</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{t('apartments.title')}</Typography>
          {isUnionAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              {t('apartments.addApartment')}
            </Button>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : apartments.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>{t('apartments.noApartments')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('apartments.addFirst')}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              {t('apartments.addFirstButton')}
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('apartments.unitNumber')}</TableCell>
                  <TableCell>{t('apartments.floor')}</TableCell>
                  <TableCell align="right">{t('apartments.areaSqm')}</TableCell>
                  <TableCell align="right">{t('apartments.sharePercent')}</TableCell>
                  <TableCell align="right">{t('apartments.residents')}</TableCell>
                  <TableCell align="center">{t('apartments.owners')}</TableCell>
                  <TableCell>{t('apartments.lastInvoice')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apartments.map((apartment) => (
                  <TableRow key={apartment.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {apartment.unit_number}
                      </Typography>
                      {apartment.apartment_tunnus && (
                        <Typography variant="caption" color="text.secondary">
                          {apartment.apartment_tunnus}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{apartment.floor || '-'}</TableCell>
                    <TableCell align="right">{apartment.area_sqm}</TableCell>
                    <TableCell align="right">{apartment.ownership_share_percent}%</TableCell>
                    <TableCell align="right">{apartment.registered_residents_count}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={apartment.owner_count || 0}
                        size="small"
                        color={apartment.owner_count > 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {apartment.last_invoice_sent ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(apartment.last_invoice_sent.sent_at).toLocaleDateString()}
                          </Typography>
                          <Tooltip title={t('sentInvoices.title')}>
                            <IconButton size="small" onClick={() => navigate(`/sent-invoices?apartment=${apartment.id}`)}>
                              <OpenInNewIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('apartments.viewDetails')}>
                        <IconButton size="small" onClick={() => handleView(apartment.id)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {isUnionAdmin && (
                        <>
                          <Tooltip title={t('apartments.editApartment')}>
                            <IconButton size="small" onClick={() => handleEdit(apartment.id)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('apartments.invoice')}>
                            <IconButton size="small" onClick={() => openInvoiceDialog(apartment)}>
                              <ReceiptIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Invoice dialog */}
      <Dialog open={invoiceDialog.open} onClose={closeInvoiceDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('apartments.invoiceApt', { number: invoiceDialog.apartment?.unit_number })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {t('apartments.invoiceDesc')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('apartments.year')}
              type="number"
              value={invoiceYear}
              onChange={(e) => setInvoiceYear(parseInt(e.target.value) || THIS_YEAR)}
              inputProps={{ min: 2000, max: 2100 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('apartments.month')}
              type="number"
              value={invoiceMonth}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (v >= 1 && v <= 12) setInvoiceMonth(v);
              }}
              inputProps={{ min: 1, max: 12 }}
              sx={{ flex: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInvoiceDialog} disabled={previewing || sending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="outlined"
            startIcon={previewing ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
            onClick={handlePreview}
            disabled={previewing || sending}
          >
            {previewing ? t('apartments.generating') : t('apartments.preview')}
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleSend}
            disabled={previewing || sending}
          >
            {sending ? t('apartments.sending') : t('apartments.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default Apartments;
